/**
Copyright (c) 2022-2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useRef, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import {
  CommandLineIcon,
  XMarkIcon,
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/react/24/outline';
import { vmApi } from '../../services/vmApi';

interface VMWebTerminalProps {
  vmId: string;
  projectId?: string;
  isRunning: boolean;
  onClose?: () => void;
  minimal?: boolean; // Hide header/controls for standalone full-screen mode
}

export interface VMWebTerminalHandle {
  disconnect: () => void;
}

interface TerminalMessage {
  type: 'input' | 'output' | 'resize' | 'connected' | 'error';
  data?: string;
  cols?: number;
  rows?: number;
  error?: string;
}

const VMWebTerminal = forwardRef<VMWebTerminalHandle, VMWebTerminalProps>(({
  vmId,
  projectId,
  isRunning,
  onClose,
  minimal = false,
}, ref) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstanceRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Send resize message to backend
  const sendResize = useCallback((cols: number, rows: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message: TerminalMessage = {
        type: 'resize',
        cols,
        rows,
      };
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Connect to WebSocket terminal
  const connect = useCallback(() => {
    if (!isRunning) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setIsConnecting(true);
    setError(null);

    const wsUrl = vmApi.getTerminalUrl(vmId, projectId);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);

        // Send initial size
        if (terminalInstanceRef.current) {
          sendResize(
            terminalInstanceRef.current.cols,
            terminalInstanceRef.current.rows
          );
        }
      };

      ws.onmessage = (event) => {
        try {
          const message: TerminalMessage = JSON.parse(event.data);

          switch (message.type) {
            case 'connected':
              // Don't write "Connected to VM" - let the shell prompt show instead
              // terminalInstanceRef.current?.write('\r\n\x1b[32mConnected to VM\x1b[0m\r\n\r\n');
              break;
            case 'output':
              if (message.data && terminalInstanceRef.current) {
                // Write output directly to terminal
                terminalInstanceRef.current.write(message.data);
              }
              break;
            case 'error':
              setError(message.error || 'Terminal error');
              if (terminalInstanceRef.current) {
                terminalInstanceRef.current.write(
                  `\r\n\x1b[31mError: ${message.error}\x1b[0m\r\n`
                );
              }
              break;
          }
        } catch (e) {
          // If not JSON, treat as raw output
          if (terminalInstanceRef.current && event.data) {
            terminalInstanceRef.current.write(event.data);
          }
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection error');
        setIsConnected(false);
        setIsConnecting(false);
      };

      ws.onclose = () => {
        setIsConnected(false);
        setIsConnecting(false);
        terminalInstanceRef.current?.write('\r\n\x1b[33mDisconnected\x1b[0m\r\n');

        // Don't auto-reconnect terminal - user should explicitly reconnect
      };
    } catch (err) {
      setError('Failed to connect to terminal');
      setIsConnecting(false);
    }
  }, [vmId, projectId, isRunning, sendResize]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Expose disconnect method via ref
  useImperativeHandle(ref, () => ({
    disconnect,
  }), [disconnect]);

  // Initialize terminal
  useEffect(() => {
    if (!terminalRef.current || terminalInstanceRef.current) return;

    const terminal = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#ffffff',
        cursorAccent: '#1e1e1e',
        selectionBackground: '#264f78',
        black: '#000000',
        red: '#cd3131',
        green: '#0dbc79',
        yellow: '#e5e510',
        blue: '#2472c8',
        magenta: '#bc3fbc',
        cyan: '#11a8cd',
        white: '#e5e5e5',
        brightBlack: '#666666',
        brightRed: '#f14c4c',
        brightGreen: '#23d18b',
        brightYellow: '#f5f543',
        brightBlue: '#3b8eea',
        brightMagenta: '#d670d6',
        brightCyan: '#29b8db',
        brightWhite: '#ffffff',
      },
      scrollback: 10000,
      allowProposedApi: true,
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.open(terminalRef.current);
    fitAddon.fit();

    terminalInstanceRef.current = terminal;
    fitAddonRef.current = fitAddon;

    // Handle terminal input
    terminal.onData((data) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const message: TerminalMessage = {
          type: 'input',
          data,
        };
        wsRef.current.send(JSON.stringify(message));
      }
    });

    // Handle terminal resize
    terminal.onResize(({ cols, rows }) => {
      sendResize(cols, rows);
    });

    // Welcome message
    terminal.write('Welcome to Podstack VM Terminal\r\n');
    terminal.write('Connecting to VM...\r\n\r\n');

    // Connect to WebSocket
    connect();

    // Handle window resize
    const handleResize = () => {
      if (fitAddonRef.current) {
        fitAddonRef.current.fit();
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      disconnect();
      terminal.dispose();
      terminalInstanceRef.current = null;
      fitAddonRef.current = null;
    };
  }, [connect, disconnect, sendResize]);

  // Reconnect when isRunning changes
  useEffect(() => {
    if (isRunning && !isConnected && !isConnecting) {
      connect();
    } else if (!isRunning) {
      disconnect();
    }
  }, [isRunning, isConnected, isConnecting, connect, disconnect]);

  // Refit terminal when fullscreen changes
  useEffect(() => {
    setTimeout(() => {
      if (fitAddonRef.current) {
        fitAddonRef.current.fit();
      }
    }, 100);
  }, [isFullscreen]);

  const handleReconnect = () => {
    disconnect();
    terminalInstanceRef.current?.clear();
    terminalInstanceRef.current?.write('Reconnecting...\r\n\r\n');
    setTimeout(connect, 500);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!isRunning) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <CommandLineIcon className="w-5 h-5 text-gray-400" />
          <h3 className="text-base font-semibold text-white">Terminal</h3>
        </div>
        <div className="text-center py-8 bg-gray-800 rounded-lg">
          <CommandLineIcon className="w-10 h-10 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">VM is not running</p>
          <p className="text-xs text-gray-500">Start the VM to use terminal</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${
        minimal
          ? 'h-full w-full bg-gray-900'
          : isFullscreen
          ? 'fixed inset-0 z-50 bg-gray-900'
          : 'card overflow-hidden'
      }`}
    >
      {/* Header - Hidden in minimal mode */}
      {!minimal && (
      <div className={`flex items-center justify-between ${isFullscreen ? 'p-3 bg-gray-800' : 'mb-3'}`}>
        <div className="flex items-center gap-2">
          <CommandLineIcon className="w-5 h-5 text-green-400" />
          <h3 className="text-base font-semibold text-white">
            Terminal
          </h3>
          {isConnected ? (
            <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/20 px-1.5 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
              Connected
            </span>
          ) : isConnecting ? (
            <span className="flex items-center gap-1 text-xs text-yellow-400 bg-yellow-500/20 px-1.5 py-0.5 rounded-full">
              <ArrowPathIcon className="w-3 h-3 animate-spin" />
              Connecting
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-red-400 bg-red-500/20 px-1.5 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
              Disconnected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReconnect}
            className="p-1.5 rounded hover:bg-gray-800 text-gray-400 hover:text-gray-300"
            title="Reconnect"
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-1.5 rounded hover:bg-gray-800 text-gray-400 hover:text-gray-300"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <ArrowsPointingInIcon className="w-4 h-4" />
            ) : (
              <ArrowsPointingOutIcon className="w-4 h-4" />
            )}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-gray-800 text-gray-400 hover:text-gray-300"
              title="Close terminal"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      )}

      {/* Terminal container */}
      <div
        ref={terminalRef}
        className={`${
          minimal
            ? 'h-full w-full'
            : isFullscreen
            ? 'h-[calc(100vh-56px)]'
            : 'h-80 rounded-lg overflow-hidden'
        }`}
        style={{ backgroundColor: '#1e1e1e' }}
      />

      {/* Error message */}
      {error && !isConnected && (
        <div className="absolute bottom-4 left-4 right-4 p-3 bg-red-100 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={handleReconnect}
            className="mt-2 text-xs text-red-600 hover:text-red-700 underline"
          >
            Try reconnecting
          </button>
        </div>
      )}
    </div>
  );
});

VMWebTerminal.displayName = 'VMWebTerminal';

export default VMWebTerminal;
